import { t } from '@lingui/macro'
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { AspenCandidatesService, CandidateDTO } from '../client'

interface CandidateSearchDropdownProps {
    value: CandidateDTO | null
    onChange: (candidate: CandidateDTO | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    error?: boolean
    helperText?: string
}

export const CandidateSearchDropdown: React.FC<CandidateSearchDropdownProps> = ({
    value,
    onChange,
    label = t`Assigned Candidate`,
    placeholder = t`Search candidates by name, DOB, or phone...`,
    disabled = false,
    error = false,
    helperText
}) => {
    const [inputValue, setInputValue] = useState('')
    const [options, setOptions] = useState<CandidateDTO[]>([])
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const hasInitialLoad = useRef(false)
    const isInitialMount = useRef(true)

    console.log(t`CandidateSearchDropdown rendered with:`, { inputValue, options: options.length, loading, searchQuery })

    // Load initial candidates when component mounts
    useEffect(() => {
        if (hasInitialLoad.current) return // Prevent duplicate calls in StrictMode

        const loadInitialCandidates = async () => {
            console.log(t`Loading initial candidates...`)
            hasInitialLoad.current = true
            setLoading(true)
            setCurrentPage(1)
            setHasMore(true)
            try {
                const response = await AspenCandidatesService.indexCandidatesGet({
                    page: 1,
                    limit: 50,
                    sort: 'first_name:asc'
                })
                const candidates = response.data || []
                setHasMore(response.meta ? response.meta.currentPage < response.meta.totalPages : false)
                setOptions(candidates)
            } catch (error) {
                console.error(t`Error loading initial candidates:`, error)
                setOptions([])
                setHasMore(false)
            } finally {
                setLoading(false)
            }
        }

        loadInitialCandidates()
    }, [])

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            console.log(t`Setting search query to:`, inputValue)
            setSearchQuery(inputValue)
        }, 200) // Reduced from 300ms for better responsiveness

        return () => {
            clearTimeout(timer)
        }
    }, [inputValue])

    // Fetch candidates when search query changes (skip initial empty string)
    useEffect(() => {
        // Skip on initial mount when searchQuery is empty (initial load effect handles that)
        if (isInitialMount.current) {
            if (searchQuery === '') {
                isInitialMount.current = false
                return
            }
            isInitialMount.current = false
        }

        // Skip if searchQuery is empty and we already have options (no need to refetch)
        if (!searchQuery.trim() && options.length > 0) {
            return
        }

        const fetchCandidates = async () => {
            setLoading(true)
            setCurrentPage(1)
            setHasMore(true)

            // Clear previous results immediately when starting a new search
            if (searchQuery && searchQuery.trim()) {
                setOptions([])
            }

            try {
                // Build the API call parameters
                const params: any = {
                    page: 1,
                    limit: 50,
                    sort: 'first_name:asc'
                }

                // Add search parameter if there's a search query
                if (searchQuery && searchQuery.trim()) {
                    params.search = searchQuery.trim()
                }

                const response = await AspenCandidatesService.indexCandidatesGet(params)
                const candidates = response.data || []
                setHasMore(response.meta ? response.meta.currentPage < response.meta.totalPages : false)
                setOptions(candidates)
            } catch (error) {
                console.error(t`Error fetching candidates:`, error)
                setOptions([])
                setHasMore(false)
            } finally {
                setLoading(false)
            }
        }

        // Fetch candidates when searchQuery changes
        fetchCandidates()
    }, [searchQuery])

    // Load more candidates when scrolling
    const loadMoreCandidates = async () => {
        if (loadingMore || !hasMore || loading) return

        setLoadingMore(true)
        try {
            const nextPage = currentPage + 1
            const params: any = {
                page: nextPage,
                limit: 50,
                sort: 'first_name:asc'
            }

            if (searchQuery && searchQuery.trim()) {
                params.search = searchQuery.trim()
            }

            const response = await AspenCandidatesService.indexCandidatesGet(params)
            const newCandidates = response.data || []
            setHasMore(response.meta ? response.meta.currentPage < response.meta.totalPages : false)
            setCurrentPage(nextPage)
            setOptions((prev) => [...prev, ...newCandidates])
        } catch (error) {
            console.error(t`Error loading more candidates:`, error)
        } finally {
            setLoadingMore(false)
        }
    }

    // Handle scroll event in the listbox
    const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
        const target = event.currentTarget
        const scrollTop = target.scrollTop
        const scrollHeight = target.scrollHeight
        const clientHeight = target.clientHeight

        // Load more when user scrolls to within 100px of the bottom
        if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore && !loading) {
            loadMoreCandidates()
        }
    }

    const getOptionLabel = (option: CandidateDTO) => {
        if (!option) return ''
        return `${option.firstName || ''} ${option.lastName || ''}`.trim() || t`Unknown Candidate`
    }

    const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
        console.log(t`Input changed to:`, newInputValue)
        setInputValue(newInputValue)

        // Clear results immediately when starting a new search
        if (newInputValue.trim() && options.length > 0) {
            console.log(t`Clearing results immediately for new input...`)
            setOptions([])
        }
    }

    return (
        <Autocomplete
            value={value}
            onChange={(event, newValue) => onChange(newValue)}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            options={options}
            getOptionLabel={getOptionLabel}
            loading={loading}
            disabled={disabled}
            filterOptions={(x) => x} // Disable client-side filtering since we're using server-side search
            noOptionsText={loading ? t`Searching...` : searchQuery ? t`No candidates found` : t`Type to search candidates`}
            ListboxProps={{
                onScroll: handleScroll,
                style: { maxHeight: '300px' }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading || loadingMore ? <CircularProgress color='inherit' size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
            renderOption={(props, option) => {
                const isLastOption = options.indexOf(option) === options.length - 1
                return (
                    <Box
                        component='li'
                        {...props}
                        key={option.id}
                        onMouseEnter={() => {
                            // Preload next page when hovering over last 5 items
                            if (isLastOption && hasMore && !loadingMore && !loading) {
                                const index = options.indexOf(option)
                                if (options.length - index <= 5) {
                                    loadMoreCandidates()
                                }
                            }
                        }}
                    >
                        <Box>
                            <Typography variant='body2'>
                                {option.firstName} {option.lastName}
                            </Typography>
                            {(option.dateOfBirth || option.phone) && (
                                <Typography variant='caption' color='text.secondary'>
                                    {option.dateOfBirth && `${t`DOB:`} ${new Date(option.dateOfBirth).toLocaleDateString()}`}
                                    {option.dateOfBirth && option.phone && ' • '}
                                    {option.phone && `${t`Phone:`} ${option.phone}`}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )
            }}
        />
    )
}
